import { Inject, Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { CommonLibraryEnvironment, PermissionDto, RoleDto, UserDto, } from "./models";
import { HttpClient } from "@angular/common/http";
import { LIB_ENVIRONMENT } from "./environment.token";

@Injectable({
  providedIn: "root",
})
/**
 * HTTP client for user, role and permission administration across the Loans API
 * (backoffice users) and the Wallet API (wallet user bootstrap).
 */
export class UserService {

  /** Base URL of the Loans API users endpoints for the current environment. */
  get baseurl(): string {
    return this.env.apiUrl + "/api/v1/users";
  }

  /** Base URL of the Wallet API users endpoints for the current environment. */
  get baseurlWallet(): string {
    return this.env.apiWalletUrl + "/api/users";
  }

  constructor(
    @Inject(LIB_ENVIRONMENT) private env: CommonLibraryEnvironment,
    private http: HttpClient,
  ) {}


  /**
   * Bootstraps the caller as a Loans API user (assigns default roles on first call).
   * @returns Observable of the caller's user record.
   */
  login(): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.baseurl}/login`, {});
  }

  /**
   * Bootstraps the caller as a Wallet API user. Required before any other Wallet API
   * call — assigns the Customer role when the user has none.
   * @returns Observable of the caller's user record.
   */
  loginWallet(): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.baseurlWallet}/login`, {});
  }

  /**
   * Gets a user by id.
   * @param id User identifier.
   * @returns Observable of the user.
   */
  getUserById(id: string): Observable<UserDto> {
    return this.http.get<UserDto>(`${this.baseurl}/${id}`);
  }

  /**
   * Lists all users.
   * @returns Observable of all user records.
   */
  getAllUsers(): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.baseurl);
  }

  /**
   * Updates an existing user.
   * @param user User payload; its userId selects the record to update.
   * @returns Observable of the updated user.
   */
  updateUser(user: UserDto): Observable<UserDto> {
    return this.http.post<UserDto>(`${this.baseurl}/${user.userId}`, user);
  }

  /**
   * Creates a new user.
   * @param user User payload to create.
   * @returns Observable of the created user.
   */
  createUser(user: UserDto): Observable<UserDto> {
    return this.http.post<UserDto>(this.baseurl, user);
  }

  /**
   * Deletes a user.
   * @param id User identifier to delete.
   * @returns Observable completing when the user is deleted.
   */
  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.baseurl}/${id}`);
  }

  /**
   * Lists all assignable permissions.
   * @returns Observable of permission records.
   */
  getPermissions(): Observable<PermissionDto[]> {
    return this.http.get<PermissionDto[]>(`${this.baseurl}/permissions`);
  }

  /**
   * Lists all roles.
   * @returns Observable of role records.
   */
  getRoles(): Observable<RoleDto[]> {
    return this.http.get<RoleDto[]>(`${this.baseurl}/roles`);
  }

  /**
   * Gets a role by name.
   * @param name Role name.
   * @returns Observable of the role.
   */
  getRole(name: string): Observable<RoleDto> {
    return this.http.get<RoleDto>(`${this.baseurl}/roles/${name}`);
  }

  /**
   * Creates a new role.
   * @param role Role payload to create.
   * @returns Observable of the created role.
   */
  createRole(role: RoleDto): Observable<RoleDto> {
    return this.http.post<RoleDto>(`${this.baseurl}/roles`, role);
  }

  /**
   * Updates an existing role.
   * @param role Role payload; its name selects the record to update.
   * @returns Observable of the updated role.
   */
  updateRole(role: RoleDto): Observable<RoleDto> {
    return this.http.post<RoleDto>(`${this.baseurl}/roles/${role.name}`, role);
  }

  /**
   * Deletes a role.
   * @param roleName Name of the role to delete.
   * @returns Observable of the deleted role.
   */
  deleteRole(roleName: string): Observable<RoleDto> {
    return this.http.delete<RoleDto>(`${this.baseurl}/roles/${roleName}`);
  }
}
